import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, finalize, map, Observable, of, switchMap } from 'rxjs';

import { IntegrationChecklistStore } from '../integration-checklist/integration-checklist.store';
import { Dialog } from '../services/dialog';
import { Notification } from '../services/notification';
import {
  GoLiveResult,
  mapGoLiveFeedback,
  MerchantEnvironmentMode,
} from './merchant-environment-mode';
import { MerchantEnvironmentService } from './merchant-environment.service';

@Injectable({ providedIn: 'root' })
export class MerchantEnvironmentStore {
  private readonly service = inject(MerchantEnvironmentService);
  private readonly checklist = inject(IntegrationChecklistStore);
  private readonly dialog = inject(Dialog);
  private readonly notify = inject(Notification);
  private readonly router = inject(Router);

  private readonly _switching = signal(false);
  private readonly _lastGoLiveResult = signal<GoLiveResult | null>(null);

  readonly switching = this._switching.asReadonly();
  readonly lastGoLiveResult = this._lastGoLiveResult.asReadonly();
  readonly mode = computed<MerchantEnvironmentMode>(() =>
    this.checklist.isLive() ? 'live' : 'sandbox',
  );
  readonly isLive = this.checklist.isLive;

  requestModeChange(target: MerchantEnvironmentMode): void {
    if (this._switching() || target === this.mode()) {
      return;
    }

    if (target === 'live') {
      this.confirmSwitchToLive();
      return;
    }

    this.confirmSwitchToSandbox();
  }

  private confirmSwitchToLive(): void {
    if (!this.checklist.isGoLiveEligible()) {
      this.notify.error(
        'Not eligible for live mode',
        'Complete all integration checklist steps before going live.',
      );
      return;
    }

    this.dialog
      .openConfirmDialog(
        'Turn Go Live ON?',
        'You are about to enable go live mode. Real transactions will be processed and funds will move. Your production environment will be provisioned. Are you sure you want to continue?',
        'Go Live ON',
        'Cancel',
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.goLive().subscribe();
        }
      });
  }

  private confirmSwitchToSandbox(): void {
    this.dialog
      .openConfirmDialog(
        'Turn On Sandbox?',
        'You are about to turn go live off and return to sandbox mode. Transactions will be simulated and no real money will move. Are you sure you want to continue?',
        'SANDBOX ON',
        'Cancel',
      )
      .afterClosed()
      .subscribe((confirmed) => {
        if (confirmed) {
          this.activateSandbox().subscribe();
        }
      });
  }

  goLive(): Observable<boolean> {
    this._switching.set(true);

    return this.service.goLive().pipe(
      switchMap((response) => {
        const feedback = mapGoLiveFeedback(response);

        if (response?.success) {
          this._lastGoLiveResult.set(response.data ?? null);
          this.notify.success(feedback.title, feedback.message);

          return this.checklist.loadChecklist().pipe(
            map(() => {
              const requiresKyc = response.data?.requiresKyc ?? true;
              if (requiresKyc) {
                this.router.navigate(['/kyc/business-information']);
              }
              return true;
            }),
          );
        }

        this._lastGoLiveResult.set(null);
        this.notify.error(feedback.title, feedback.message);
        return of(false);
      }),
      catchError(() => {
        this._lastGoLiveResult.set(null);
        this.notify.error(
          'Something went wrong',
          'Failed to switch to live mode. Please try again.',
        );
        return of(false);
      }),
      finalize(() => this._switching.set(false)),
    );
  }

  activateSandbox(): Observable<boolean> {
    this._switching.set(true);

    return this.service.activateSandbox().pipe(
      switchMap((response) => {
        if (response?.success) {
          this.notify.success(
            'SANDBOX ON',
            response.message || 'Sandbox mode is now enabled.',
          );
          return this.checklist.loadChecklist().pipe(map(() => true));
        }

        this.notify.error(
          'Error',
          response?.message || 'Failed to switch to sandbox mode.',
        );
        return of(false);
      }),
      catchError(() => {
        this.notify.error('Error', 'Failed to switch to sandbox mode.');
        return of(false);
      }),
      finalize(() => this._switching.set(false)),
    );
  }
}
